import React from 'react';
import { useGlobalContext } from '../../../provider/global/GlobalContext';
import { MediaApi } from '@frontastic/common';
import { useMedia } from 'react-use';

import getTranslation from '@frontastic/catwalk/src/js/getTranslation';
import { PreloadImage } from './PreloadImage';

const getInputImageDimensions = (mediaApi, props, media = null) => {
  const { forceWidth, forceHeight, width, height } = props;
  const cropRatio = media
    ? mediaApi.getFloatRatio(media, props.cropRatio)
    : null;

  if (forceWidth || forceHeight) {
    return [
      forceWidth || (cropRatio ? forceHeight / cropRatio : null),
      forceHeight || (cropRatio ? forceWidth * cropRatio : null),
    ];
  }

  // On initial load it can happen, especially in production, that
  // props.width and height are NULL. This caused a bunch of issues with
  // images being only a few pixels tall. So we do a NULL check on that.
  // If that's the case we check if the media object is there and if we
  // can use the metadata in there. Lastly it falls back to a device
  // default.
  let inputHeight = height || null;
  let inputWidth = width || null;

  if (!inputWidth && inputHeight && cropRatio) {
    inputWidth = inputHeight / cropRatio;
  } else if (!inputWidth && !inputHeight) {
    inputWidth = props.deviceType === 'mobile' ? 860 : 1680;
  }

  return [inputWidth, inputHeight];
};

export const ResponsiveImage = ({
  mobileSource = {},
  source,
  src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
  className = '',
  loading = '',
  preload = false,
  ...rest
}) => {
  const mediaApi = new MediaApi();
  const isMobile = useMedia('(max-width: 680px)', false);
  const { configuration, locale, defaultLanguage } = useGlobalContext();
  const getCropRatio = (media) => {
    if (rest.ratio === 'custom') {
      return null;
    }

    if (rest.ratio && rest.ratio !== '') {
      return rest.ratio;
    }

    return media.ratio === 'custom' ? null : media.ratio;
  };

  const getOptions = (media) => {
    return {
      gravity: media.gravity,
      ...rest.options,
    };
  };

  const getTitle = (media) => {
    if (rest.title && rest.title !== '') return rest.title;

    const translatedTitle = getTranslation(
      media.title,
      locale,
      defaultLanguage,
    );

    if (translatedTitle.translated || translatedTitle.text)
      return translatedTitle.text;
    return media?.media?.name;
  };

  const mediaSourceByDevice = {
    mobile: '(max-width: 680px)',
    desktop: '(min-width: 681px)',
  };

  const getSrcSet = (mediaSource, mediaWidth = null, mobile = false) => {
    if (!mediaSource.media) return;
    const [width, height] = getInputImageDimensions(
      mediaApi,
      rest,
      mediaSource,
    );
    const cropRatio = getCropRatio(mediaSource);
    let options = {
      ...getOptions(mediaSource),
      quality: 'auto',
    };
    const factors = mobile ? [1] : [1, 2];
    return factors
      .map((factor) => {
        return [
          mediaApi.getImageLink(
            mediaSource.media,
            configuration,
            mediaWidth ? 'auto' : width,
            height,
            cropRatio,
            options,
            factor,
          ),
          factor + 'x',
        ].join(' ');
      })
      .join(', ');
  };

  if (!source && !source.media) {
    return null;
  }

  const desktopSrcSet = getSrcSet(source, mediaSourceByDevice['desktop']);
  const mobileSrcSet = getSrcSet(
    mobileSource?.media ? mobileSource : source,
    mediaSourceByDevice['mobile'],
    true,
  );

  const renderSource = (srcSet, mediaSource) => {
    if (!srcSet) return null;
    return <source srcSet={srcSet} media={mediaSource} />;
  };

  return (
    <>
      <PreloadImage
        condition={preload}
        imageSrcSet={isMobile ? mobileSrcSet : desktopSrcSet}
      />
      <picture>
        {renderSource(desktopSrcSet, mediaSourceByDevice['desktop'])}
        {renderSource(mobileSrcSet, mediaSourceByDevice['mobile'])}
        <img
          width={source?.media?.width}
          height={source?.media?.height}
          fetchPriority={preload ? 'high' : 'auto'}
          loading={loading}
          alt={getTitle(source) ?? 'responsive image'}
          srcSet={src}
          className={className}
        />
      </picture>
    </>
  );
};
